import React, { useState } from 'react';
import { Mail, MapPin, Send, MessageSquare, Clock, AlertCircle, Users } from 'lucide-react';
import { saveContactMessage } from '../../services/contactService';

interface ContactPageProps {
  theme: 'light' | 'dark';
}

export const ContactPage: React.FC<ContactPageProps> = ({ theme }) => {
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    purpose: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const purposes = [
    'Bug Report',
    'Feature Request',
    'General Inquiry',
    'Partnership',
    'Feedback',
    'Other'
  ];

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.purpose) {
      setError('Please select a purpose of contact');
      return;
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      setError('Please enter a message (at least 10 characters)');
      return;
    }

    setLoading(true);
    console.log('📤 ContactPage: Submitting form...', formData);

    try {
      // Save using the contact service (Supabase + localStorage)
      const newMessage = await saveContactMessage({
        name: formData.name,
        email: formData.email,
        purpose: formData.purpose,
        message: formData.message
      });

      if (newMessage) {
        console.log('✅ ContactPage: Message submitted successfully');
        
        // Dispatch event for admin inbox to update
        window.dispatchEvent(new CustomEvent('contact_message_sent', { detail: newMessage }));
        
        setSubmitted(true);
        setFormData({ name: '', email: '', purpose: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        console.error('❌ ContactPage: Failed to save message');
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('❌ ContactPage: Form submission error:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-8 sm:py-12 md:py-16">
        {/* Header */}
        <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20">
            <MessageSquare className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
            <span className="text-xs sm:text-sm font-medium text-red-500">Get in Touch</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
            <span className={isDark ? 'text-white' : 'text-zinc-900'}>Contact Us</span>
          </h1>

          <p className={`text-base sm:text-lg max-w-2xl ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Have questions, feedback, or want to collaborate? We'd love to hear from you!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-4 sm:space-y-6">
            <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Contact Information
            </h2>

            <div className="space-y-3 sm:space-y-4">
              {[
                {
                  icon: Mail,
                  label: 'Email',
                  value: 'nabidahamed2003@gmail.com',
                  href: 'mailto:nabidahamed2003@gmail.com',
                  desc: 'We respond within 24 hours'
                },
                {
                  icon: MapPin,
                  label: 'Location',
                  value: 'Rajshahi, Bangladesh',
                  href: '#',
                  desc: 'RUET Campus'
                },
                {
                  icon: Clock,
                  label: 'Response Time',
                  value: 'Within 24 Hours',
                  href: '#',
                  desc: 'Average response time'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <a
                    key={idx}
                    href={item.href}
                    className={`p-3 sm:p-4 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-zinc-900/50 border-zinc-800 hover:border-red-500/50 hover:bg-red-500/5'
                        : 'bg-white border-zinc-200 hover:border-red-300 hover:bg-red-50/50'
                    }`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isDark ? 'bg-red-500/20' : 'bg-red-100'
                      }`}>
                        <Icon className="w-5 sm:w-6 h-5 sm:h-6 text-red-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                          {item.label}
                        </h3>
                        <p className="text-xs sm:text-sm font-mono text-red-500 mt-1 break-all">
                          {item.value}
                        </p>
                        <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Social Links */}
            <div className={`p-4 sm:p-6 rounded-2xl border ${
              isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-50 border-zinc-200'
            }`}>
              <h3 className={`font-semibold mb-3 sm:mb-4 text-sm sm:text-base ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                Connect With Us
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                {['GitHub', 'Twitter', 'LinkedIn', 'Discord'].map((social, idx) => (
                  <a
                    key={idx}
                    href="#"
                    className={`px-2 sm:px-4 py-2 rounded-lg text-center text-xs sm:text-sm font-medium transition-colors ${
                      isDark
                        ? 'bg-zinc-800 hover:bg-red-500/20 text-zinc-300 hover:text-red-400'
                        : 'bg-zinc-100 hover:bg-red-100 text-zinc-700 hover:text-red-600'
                    }`}
                  >
                    {social}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`p-4 sm:p-6 rounded-2xl border ${
            isDark ? 'bg-zinc-900/30 border-zinc-800' : 'bg-zinc-50/50 border-zinc-200'
          }`}>
            <h2 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Send us a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                  }`}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                  }`}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Purpose of Contact *
                </label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                  }`}
                >
                  <option value="">Select a purpose...</option>
                  {purposes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                  rows={4}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-colors resize-none text-sm sm:text-base ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20'
                  }`}
                  placeholder="Tell us more about your inquiry..."
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                  Minimum 10 characters required
                </p>
              </div>

              {error && (
                <div className={`p-3 sm:p-4 rounded-lg text-sm sm:text-base flex items-center gap-2 ${
                  isDark
                    ? 'bg-red-500/20 border border-red-500/50 text-red-300'
                    : 'bg-red-100 border border-red-300 text-red-700'
                }`}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {submitted && (
                <div className={`p-3 sm:p-4 rounded-lg text-sm sm:text-base flex items-center gap-2 ${
                  isDark
                    ? 'bg-green-500/20 border border-green-500/50 text-green-300'
                    : 'bg-green-100 border border-green-300 text-green-700'
                }`}>
                  ✓ Message sent successfully! We'll get back to you soon.
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-red-600 via-rose-600 to-orange-600 text-white rounded-lg font-semibold text-sm sm:text-base hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-red-500/25"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 sm:mt-16 md:mt-20">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            Frequently Asked Questions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {[
              {
                q: 'How long does it take to get a response?',
                a: 'We aim to respond to all inquiries within 24 hours during business days.'
              },
              {
                q: 'Can I use NanoLez for free?',
                a: 'Yes! NanoLez is completely free to use. Start creating your first roadmap today.'
              },
              {
                q: 'How do I report a bug?',
                a: 'Email us at nabidahamed2003@gmail.com with details about the issue you found.'
              },
              {
                q: 'Can I request a new feature?',
                a: 'Absolutely! We love feedback. Share your ideas through the contact form or email us.'
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className={`p-4 sm:p-6 rounded-2xl border ${
                  isDark
                    ? 'bg-zinc-900/50 border-zinc-800'
                    : 'bg-white border-zinc-200'
                }`}
              >
                <h3 className={`font-semibold mb-2 sm:mb-3 text-sm sm:text-base ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {item.q}
                </h3>
                <p className={`text-sm sm:text-base ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Who Built This Section */}
        <div className={`mt-12 sm:mt-16 md:mt-20 p-6 sm:p-8 md:p-12 rounded-3xl border ${
          isDark ? 'bg-gradient-to-br from-red-500/5 to-orange-500/5 border-red-500/20' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200'
        }`}>
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-red-600 to-orange-600 rounded-3xl flex items-center justify-center shadow-xl shadow-red-500/30">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>🔥 Who Built This</h2>
              <p className={`text-sm max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                Built by someone obsessed with transformation. Who wanted to crack the code of identity-based learning.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>👨‍💻 Creator's Mission</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  <span className="font-semibold">Nabid Ahammed Limon</span> is a B.URP student at RUET who discovered something: 
                  most people don't lack ability—they lack identity. He built NanoLez to weaponize that insight. 
                  Now every learner can become who they actually are.
                </p>
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/50 border-zinc-200'}`}>
                  <p className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'} mb-2`}>🎯 Expertise:</p>
                  <ul className={`text-sm space-y-1 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>✓ Full-stack engineering (React, Node.js, TypeScript)</li>
                    <li>✓ AI transformation & identity psychology</li>
                    <li>✓ Learning science that actually converts</li>
                    <li>✓ UX architecture for behavior change</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>🔥 NanoLez DNA</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  NanoLez Tech isn't just another edtech. It's identity engineering. 
                  We use AI + psychology to rebuild who you are, one microburst of learning at a time. 
                  We don't sell features. We sell transformation.
                </p>
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/50 border-zinc-200'}`}>
                  <p className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'} mb-2`}>🔥 Core Beliefs:</p>
                  <ul className={`text-sm space-y-1 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>✓ Identity &gt; Skills (who you are &gt; what you know)</li>
                    <li>✓ Accessibility = Power democratization</li>
                    <li>✓ AI is identity catalyst, not replacement</li>
                    <li>✓ Your streak is your real identity</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20">
              <div className="text-center">
                <h4 className="text-lg font-bold mb-2 text-red-600 dark:text-red-400">🎯 The NanoLez Promise</h4>
                <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  We don't just teach you things. We rewire your identity. AI + psychology + your commitment = permanent evolution. 
                  This is irreversible transformation.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
              <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/50 border-zinc-200'}`}>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>📧 Let's Talk</p>
                <p className="font-semibold text-red-600 dark:text-red-400">nabidahamed2003@gmail.com</p>
              </div>
              <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/50 border-zinc-200'}`}>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>🌐 Home</p>
                <p className="font-semibold text-red-600 dark:text-red-400">nanolez.tech</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
